import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { startOfDay, subDays } from "date-fns";
import { ArrowLeft, Pencil, Trash2, RefreshCw } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import Card from "@/components/common/Card";
import Button from "@/components/common/Button";
import Modal from "@/components/common/Modal";
import StatCard from "@/components/common/StatCard";
import DateRangePicker from "@/components/common/DateRangePicker";
import { usePageTitle } from "@/components/common/AppShell";
import { createPixelsApi } from "../api";
import { formatCount, formatDateTime } from "../format";
import { rangeDays, toUtcRange } from "../range";
import PixelEmbed from "../components/PixelEmbed";
import PixelForm from "../components/PixelForm";

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend, Tooltip);

const MAX_RANGE_DAYS = 92;

const cssVar = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
};

const defaultRange = () => ({
  from: startOfDay(subDays(new Date(), 6)),
  to: startOfDay(new Date()),
});

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-(--text-secondary)">
        {title}
      </h3>
      {children}
    </section>
  );
}

function TopTable({
  title,
  rows,
  keyField,
  valueField = "hits",
  valueLabel = "Hits",
  keyLabel = "Page",
}) {
  return (
    <Card>
      <h4 className="mb-3 text-[13px] font-semibold text-(--text-primary)">
        {title}
      </h4>
      {rows.length === 0 ? (
        <p className="text-[13px] text-(--text-muted)">No data</p>
      ) : (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] font-bold uppercase tracking-wide text-(--text-muted)">
              <th className="pb-1 pr-3 font-bold">{keyLabel}</th>
              <th className="pb-1 text-right font-bold">{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${row[keyField]}-${i}`}>
                <td className="max-w-0 truncate py-1 pr-3 text-(--text-primary)">
                  <span title={row[keyField]}>{row[keyField] || "—"}</span>
                </td>
                <td className="py-1 text-right text-(--text-secondary)">
                  {formatCount(row[valueField])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export default function PixelDetailPage() {
  const { uuid } = useParams();
  const { performRequest } = useApp();
  const api = useMemo(() => createPixelsApi(performRequest), [performRequest]);
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.replace(/\/pixels.*$/, "");

  const [pixel, setPixel] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusState, setStatusState] = useState("idle"); // idle|loading|error
  const [statusError, setStatusError] = useState("");

  const [range, setRange] = useState(defaultRange);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState("");

  usePageTitle(pixel?.label ?? "Pixel");

  useEffect(() => {
    let cancelled = false;
    setLoadError("");
    api
      .get(uuid)
      .then((p) => {
        if (!cancelled) setPixel(p);
      })
      .catch(() => {
        if (!cancelled) setLoadError("Not found");
      });
    return () => {
      cancelled = true;
    };
  }, [api, uuid]);

  const rangeError =
    rangeDays(range) > MAX_RANGE_DAYS
      ? `Date range is limited to ${MAX_RANGE_DAYS} days.`
      : "";

  useEffect(() => {
    if (!pixel || rangeError) {
      setStats(null);
      return undefined;
    }
    let cancelled = false;
    const { from, to } = toUtcRange(range);
    setStatsLoading(true);
    setStatsError("");
    api
      .stats(uuid, from, to)
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch((err) => {
        if (!cancelled) {
          setStats(null);
          setStatsError(err?.message || "Failed to load stats");
        }
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, uuid, pixel, range, rangeError]);

  const checkInstall = async () => {
    setStatusState("loading");
    setStatusError("");
    try {
      setStatus(await api.status(uuid));
      setStatusState("idle");
    } catch (err) {
      setStatusState("error");
      setStatusError(err?.message || "Failed to check install");
    }
  };

  const handleSave = async (body) => {
    const updated = await api.update(uuid, body);
    setPixel(
      updated && updated.uuid ? { ...pixel, ...updated } : await api.get(uuid),
    );
    setEditing(false);
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete pixel "${pixel.label}"? It will stop collecting data immediately (within 30 seconds).`,
      )
    ) {
      return;
    }
    try {
      await api.remove(uuid);
      navigate(`${basePath}/pixels`);
    } catch (err) {
      window.alert("Failed to delete pixel: " + (err.message || "Unknown"));
    }
  };

  const daily = stats?.daily ?? [];
  const chartData = useMemo(
    () => ({
      labels: daily.map((d) => String(d.day).slice(0, 10)),
      datasets: [
        {
          label: "Hits",
          data: daily.map((d) => d.hits ?? 0),
          backgroundColor: cssVar("--brand-primary", "#6d5dfc"),
          borderRadius: 3,
        },
      ],
    }),
    [daily],
  );
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const backButton = (
    <button
      type="button"
      onClick={() => navigate(`${basePath}/pixels`)}
      className="mb-4 inline-flex items-center gap-2 border-0 bg-transparent text-[13px] text-(--text-secondary) hover:text-(--text-primary)"
    >
      <ArrowLeft size={16} /> Back to pixels
    </button>
  );

  if (loadError) {
    return (
      <section>
        {backButton}
        <Card>
          <p className="text-sm text-(--text-secondary)">{loadError}</p>
        </Card>
      </section>
    );
  }
  if (!pixel) {
    return (
      <section>
        {backButton}
        <Card>
          <p className="text-sm text-(--text-secondary)">Loading pixel…</p>
        </Card>
      </section>
    );
  }

  const domains = pixel.allowedDomains ?? [];

  return (
    <section>
      {backButton}

      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-(--text-primary)">
            {pixel.label}
          </h2>
          <p className="mt-1 font-mono text-[12px] text-(--text-muted)">
            {pixel.uuid}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setEditing(true)}>
            <Pencil size={14} /> Edit
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <Trash2 size={14} /> Delete
          </Button>
        </div>
      </div>

      <Section title="Install">
        <Card>
          <PixelEmbed pixelUuid={pixel.uuid} />
          <div className="mt-4 border-t border-(--border-subtle) pt-4">
            <p className="mb-2 text-[12px] text-(--text-muted)">
              Allowed domains:{" "}
              {domains.length ? domains.join(", ") : "any site"}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                size="small"
                onClick={checkInstall}
                disabled={statusState === "loading"}
              >
                <RefreshCw size={14} />
                {statusState === "loading" ? "Checking…" : "Check install"}
              </Button>
              {statusState === "error" && (
                <span className="text-[13px] text-(--status-danger)">
                  {statusError}
                </span>
              )}
              {statusState === "idle" && status && (
                <span className="text-[13px] text-(--text-secondary)">
                  {status.lastSeenAt
                    ? `Last hit ${formatDateTime(status.lastSeenAt)} · ${formatCount(status.events24h)} events and ${formatCount(status.devices24h)} devices in the last 24h`
                    : "No hits yet"}
                </span>
              )}
            </div>
          </div>
        </Card>
      </Section>

      <Section title="Stats">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <DateRangePicker value={range} onChange={setRange} />
          {rangeError && (
            <span className="text-[13px] text-(--status-danger)" role="alert">
              {rangeError}
            </span>
          )}
          {statsError && (
            <span className="text-[13px] text-(--status-danger)" role="alert">
              {statsError}
            </span>
          )}
        </div>

        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label="Hits (page views)"
            value={statsLoading ? "…" : formatCount(stats?.totals?.hits)}
          />
          <StatCard
            label="Beacons"
            value={statsLoading ? "…" : formatCount(stats?.totals?.beacons)}
          />
          <StatCard
            label="Unique devices"
            value={
              statsLoading ? "…" : formatCount(stats?.totals?.uniqueDevices)
            }
          />
        </div>

        <Card className="mb-4">
          <h4 className="mb-3 text-[13px] font-semibold text-(--text-primary)">
            Hits per day (UTC)
          </h4>
          {daily.length === 0 ? (
            <p className="text-[13px] text-(--text-muted)">
              {statsLoading ? "Loading…" : "No data in this range"}
            </p>
          ) : (
            <div style={{ height: 240 }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <TopTable
            title="Top pages"
            rows={stats?.topPages ?? []}
            keyField="page"
          />
          <TopTable
            title="Top hosts"
            rows={stats?.topHosts ?? []}
            keyField="host"
            valueField="beacons"
            valueLabel="Beacons"
            keyLabel="Host"
          />
        </div>
      </Section>

      <Modal
        isOpen={editing}
        onClose={() => setEditing(false)}
        title="Edit pixel"
        size="small"
      >
        {editing && (
          <PixelForm
            initialPixel={pixel}
            onSubmit={handleSave}
            onCancel={() => setEditing(false)}
          />
        )}
      </Modal>
    </section>
  );
}
